"use client";


import { useNavigation } from "@app/[locale]/(site)/navContext";
import { ArrowDownIcon, ArrowUpIcon, PlusIcon } from "@ngg/icons";
import type { ReactNode } from "react";
import { useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";

export default function Wrapper({
  label,
  open: defaultOpen = false,
  alwaysOpen = false,
  totalSelected,
  children,
  disabled,
  slug,
}: {
  children: ReactNode;
  label: string;
  open?: boolean;
  alwaysOpen?: boolean;
  totalSelected?: number | boolean;
  disabled?: boolean;
  slug?: string;
}) {
  const { flatNavigation } = useNavigation().state;
  const [open, setOpen] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (!ref.current || disabled) return;

    ref.current.style.height = "auto";
    const height = ref.current.clientHeight;

    ref.current.style.height = "";

    ref.current.style.setProperty("--height", `${height}px`);

    // Timeout is needed to allow the height to be set before the transition
    setTimeout(() => {
      setOpen(!open);
    });

    // Set height to auto after transition
    setTimeout(() => {
      if (!ref.current) return;
      ref.current.style.setProperty("--height", `auto`);
    }, 150);
  };

  const displayedLabel = useMemo(() => {
    if (label !== "Category" || !slug) return label;
    return flatNavigation?.[slug]?.title ?? "Categories";
  }, [slug, label, flatNavigation]);

  if (disabled) {
    return (
      <div className="pointer-events-none overflow-hidden py-2 opacity-50">
        <div className="flex items-center text-left text-base uppercase">
          <span className="grow px-3 py-2">{displayedLabel}</span>

          {alwaysOpen ? null : (
            <button type="button" className="px-2">
              <PlusIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden border-grey-150">
      <button
        type="button"
        className="flex h-12 w-full items-center text-left text-base font-normal"
        onClick={() => (alwaysOpen ? null : handleClick())}
      >
        <span className="flex grow items-center text-base font-normal">
          {displayedLabel}
          {totalSelected ? (
            <span className="ml-1 bg-black px-1 py-0.5 tabular-nums text-white">
              {totalSelected}
            </span>
          ) : null}
        </span>

        {alwaysOpen ? null : (
          <span className="px-2">
            {open ? (
              <ArrowUpIcon className="h-6 w-6" />
            ) : (
              <ArrowDownIcon className="h-6 w-6" />
            )}
          </span>
        )}
      </button>
      <div
        ref={ref}
        className={cn(
          "overflow-hidden transition-[height]",
          !alwaysOpen && (open ? "h-[var(--height)]" : "h-0"),
        )}
      >
        {children}
      </div>
    </div>
  );
}
